import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { getTangentOnCubicBezier } from '@canvas/bezier';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { type PathSegment, type Line, type PathStyleForLegacyCaps } from '../types.ts';

import { createCubicBezierFromSegment } from './cubic-bezier.ts';

/**
 * @deprecated
 * all legacy caps code will be removed after diagrammingArrowheads feature is rolled out
 */
export function getLinesForArrowCap(
  position: vec2,
  segmentAngle: number,
  location: 'start' | 'end',
  arrowLength: number,
) {
  const lines: Line[] = [];

  let angleOffset = location === 'start' ? 0.8 : 2.3;

  if (
    getExperimentValue('diagrammingArrowheads', 'enabled', {
      fireExperimentExposure: false,
    })
  ) {
    angleOffset = location === 'start' ? 0.8 : 2.5;
  }

  const angle = segmentAngle * -1;

  // first line for the arrow head
  const direction1 = -angle + angleOffset;
  const v1 = vec2.fromValues(
    Math.cos(direction1) * arrowLength,
    Math.sin(direction1) * arrowLength,
  );
  const end1 = vec2.fromValues(0, 0);
  vec2.add(end1, position, v1);
  lines.push({ start: position, end: end1 });

  // second line for the arrow head
  const direction2 = -angle - angleOffset;
  const v2 = vec2.fromValues(
    Math.cos(direction2) * arrowLength,
    Math.sin(direction2) * arrowLength,
  );
  const end2 = vec2.fromValues(0, 0);
  vec2.add(end2, position, v2);
  lines.push({ start: position, end: end2 });

  return lines;
}

/**
 * @deprecated
 * all legacy caps code will be removed after diagrammingArrowheads feature is rolled out
 */
export function getCapLinesForPath(
  pathStart: ReadonlyVec2,
  pathEnd: ReadonlyVec2,
  segments: PathSegment[],
  styleConfig: PathStyleForLegacyCaps,
  lines: Line[] = [],
) {
  const { startCap, endCap, stroke } = styleConfig;
  if (startCap) {
    lines.push(
      ...getLinesForArrowCap(
        vec2.clone(pathStart),
        getSegmentAngleForCapLine(segments, 'start'),
        'start',
        stroke * 10,
      ),
    );
  }

  if (endCap) {
    lines.push(
      ...getLinesForArrowCap(
        vec2.clone(pathEnd),
        getSegmentAngleForCapLine(segments, 'end'),
        'end',
        stroke * 10,
      ),
    );
  }

  return lines;
}

function getSegmentAngleForCapLine(segments: PathSegment[], location: 'start' | 'end') {
  if (segments.length === 0) {
    return 0;
  }

  if (location === 'start') {
    const firstSegment = segments[0];
    if (firstSegment.type === 'line') {
      return firstSegment.angle;
    } else if (firstSegment.type === 'arc') {
      return firstSegment.startAngle;
    } else if (firstSegment.type === 'cubic-bezier') {
      return getTangentOnCubicBezier(createCubicBezierFromSegment(firstSegment), 0);
    }
  }

  const lastSegment = segments[segments.length - 1];
  if (lastSegment.type === 'line') {
    return lastSegment.angle;
  } else if (lastSegment.type === 'arc') {
    return lastSegment.endAngle;
  } else if (lastSegment.type === 'cubic-bezier') {
    return getTangentOnCubicBezier(createCubicBezierFromSegment(lastSegment), 1);
  }

  return 0;
}
